import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { logError } from '../utils/logger';
import {
  Crown, ArrowRight, CheckCircle, Shield, Zap, MessageCircle,
  TrendingUp, FileText, Globe, Sparkles, Users, DollarSign,
  AlertTriangle, Receipt, Clock, Target, Check, ChevronDown
} from 'lucide-react';

const MetaLanding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [spotsRemaining, setSpotsRemaining] = useState(97);
  const [faqOpen, setFaqOpen] = useState(null);
  
  // UTM tracking
  const utmSource = searchParams.get('utm_source') || 'meta';
  const utmCampaign = searchParams.get('utm_campaign') || '';
  const fbclid = searchParams.get('fbclid');

  useEffect(() => {
    // Meta Pixel: PageView
    if (window.fbq) {
      window.fbq('track', 'PageView');
      window.fbq('track', 'ViewContent', {
        content_name: 'Meta Landing Page',
        content_category: 'Tax Software',
        value: 79.00,
        currency: 'USD'
      });
    }
    
    fetchSpots();
    
    // Track scroll depth
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercent > 25 && !window.scrollTracked25) {
        window.fbq && window.fbq('trackCustom', 'Scroll25');
        window.scrollTracked25 = true;
      }
      if (scrollPercent > 50 && !window.scrollTracked50) {
        window.fbq && window.fbq('trackCustom', 'Scroll50');
        window.scrollTracked50 = true;
      }
      if (scrollPercent > 75 && !window.scrollTracked75) {
        window.fbq && window.fbq('trackCustom', 'Scroll75');
        window.scrollTracked75 = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchSpots = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist/founding-count`);
      const data = await response.json();
      setSpotsRemaining(data.remaining);
    } catch (error) {
      logError(error, { context: 'MetaLanding.fetchSpots' });
    }
  };

  const handleFoundingCTA = () => {
    // Meta Pixel: InitiateCheckout
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Founding Member',
        value: 79.00,
        currency: 'USD',
        num_items: 1
      });
    }
    
    navigate(`/waitlist/founding?utm_source=${utmSource}&utm_campaign=${utmCampaign}`);
  };

  const handleWaitlistCTA = () => {
    // Meta Pixel: Lead
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: 'Waitlist'
      });
    }
    
    navigate(`/waitlist?utm_source=${utmSource}&utm_campaign=${utmCampaign}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION - Above the Fold */}
      <section className="relative bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-5xl mx-auto text-center">
          {/* Trust Badge */}
          <div className="mb-6">
            <Badge className="bg-white/10 text-white border-white/20 px-4 py-2">
              <Shield className="w-4 h-4 mr-2" />
              Powered by Stripe • 256-bit SSL Encryption
            </Badge>
          </div>

          {/* Headline - MESSAGE MATCH WITH AD */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Stop Leaving <span className="text-amber-400">$5,200</span><br />
            on the Table
          </h1>

          {/* Subheadline - Social Proof + Benefit */}
          <p className="text-xl sm:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto">
            AI finds every deduction automatically - built for <span className="text-emerald-400 font-semibold">1099 contractors</span> like you
          </p>

          {/* Hero Screenshot - AI Advisors (Key Differentiator) */}
          <div className="mb-8 relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 to-emerald-500 rounded-2xl blur opacity-30"></div>
            <img 
              src="/screenshots/advisors.png"
              alt="Choose from 4 AI Tax Advisors"
              className="relative w-full max-w-4xl mx-auto rounded-xl shadow-2xl border-2 border-white/10"
              loading="eager"
            />
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
              4 AI Advisors (Competitors have 1!)
            </div>
          </div>

          {/* Primary CTA */}
          <button
            onClick={handleFoundingCTA}
            className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-12 py-6 rounded-xl text-xl font-bold shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 mb-4 inline-flex items-center justify-center gap-3"
          >
            <Crown className="w-6 h-6" />
            Claim Your Founding Spot - $79/Year
            <ArrowRight className="w-6 h-6" />
          </button>

          {/* Urgency - Live Counter */}
          <div className="mb-8">
            <div className="inline-block bg-amber-500/20 border-2 border-amber-500 rounded-lg px-6 py-3">
              <p className="text-amber-300 font-bold text-lg">
                ⚡ Only <span className="text-2xl">{spotsRemaining}</span> of 100 Spots Left!
              </p>
              <div className="w-64 mx-auto bg-slate-800 rounded-full h-2 mt-2">
                <div 
                  className="bg-gradient-to-r from-amber-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((100 - spotsRemaining) / 100) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Trust Indicators Row */}
          <div className="flex flex-wrap justify-center gap-6 text-slate-300 text-sm">
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              30-Day Money-Back Guarantee
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              Cancel Anytime
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              No Credit Card Required
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              🇺🇸 🇪🇸 Bilingual Support
            </span>
          </div>
        </div>
      </section>

      {/* PROBLEM AMPLIFICATION SECTION */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tax Season Shouldn't Cost You Thousands
            </h2>
            <p className="text-xl text-gray-600">
              The average 1099 contractor leaves this much money on the table:
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Pain Point 1 */}
            <Card className="border-2 border-red-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <Receipt className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">$5,200</h3>
                <p className="text-lg font-semibold text-gray-800 mb-3">
                  Missed Deductions
                </p>
                <p className="text-gray-600 text-sm">
                  Home office, mileage, supplies, meals - left unclaimed every year
                </p>
              </CardContent>
            </Card>

            {/* Pain Point 2 */}
            <Card className="border-2 border-amber-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">$435+</h3>
                <p className="text-lg font-semibold text-gray-800 mb-3">
                  IRS Penalties
                </p>
                <p className="text-gray-600 text-sm">
                  Late filing, underpayment penalties, and interest charges
                </p>
              </CardContent>
            </Card>

            {/* Pain Point 3 */}
            <Card className="border-2 border-blue-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">$300-$500</h3>
                <p className="text-lg font-semibold text-gray-800 mb-3">
                  Expensive CPAs
                </p>
                <p className="text-gray-600 text-sm">
                  Per hour for tasks AI can do in minutes
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Amplification Text */}
          <div className="mt-12 text-center max-w-3xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed">
              You work hard for every dollar. Don't let the IRS keep what's rightfully yours - and don't overpay for software that should be <span className="font-bold text-emerald-600">saving you money.</span>
            </p>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF & PRICE COMPARISON */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="border-4 border-amber-400 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-amber-500 text-white px-6 py-2 font-bold text-sm">
              LIMITED TIME
            </div>
            <CardContent className="pt-12 pb-12">
              <div className="text-center mb-8">
                <div className="inline-block bg-amber-100 px-6 py-3 rounded-full mb-4">
                  <p className="text-amber-900 font-bold text-xl">
                    🔥 Founding Member Offer
                  </p>
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                  $79<span className="text-2xl text-gray-600">/year</span>
                </h2>
                <p className="text-xl text-gray-600 line-through mb-2">
                  Regular: $228/year
                </p>
                <p className="text-2xl font-bold text-emerald-600">
                  You Save: $149/Year Forever
                </p>
              </div>

              {/* Comparison Table */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="bg-slate-50 rounded-lg p-6 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">SolelyTax AI (Founding)</span>
                    <span className="text-2xl font-bold text-emerald-600">$79/year</span>
                  </div>
                  <div className="border-t border-gray-300"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Keeper Tax</span>
                    <span className="text-lg text-gray-500 line-through">$199/year</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">TurboTax Self-Employed</span>
                    <span className="text-lg text-gray-500 line-through">$179/year</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">QuickBooks Self-Employed</span>
                    <span className="text-lg text-gray-500 line-through">$360/year</span>
                  </div>
                </div>
              </div>

              {/* Live Counter */}
              <div className="text-center mb-8">
                <p className="text-lg text-gray-700 mb-3">
                  <Users className="w-5 h-5 inline mr-2" />
                  Join 3 founding members already using SolelyTax AI
                </p>
                <div className="max-w-md mx-auto">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Spots Claimed: {100 - spotsRemaining}</span>
                    <span>Remaining: {spotsRemaining}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div 
                      className="bg-gradient-to-r from-emerald-500 to-amber-500 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                      style={{ width: `${((100 - spotsRemaining) / 100) * 100}%` }}
                    >
                      <span className="text-white text-xs font-bold">{100 - spotsRemaining}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Button
                onClick={handleFoundingCTA}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-xl font-bold shadow-xl"
              >
                <Crown className="w-6 h-6 mr-2" />
                Claim Your Founding Spot - $79/Year
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>

              <p className="text-center text-gray-500 text-sm mt-4">
                30-day money-back guarantee • No credit card required to start
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* HOW IT WORKS - White Background */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-800 px-4 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              Simple & Fast
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              From Bank to Tax-Ready in 10 Minutes
            </h2>
            <p className="text-xl text-gray-600">
              No shoebox. No spreadsheet chaos. Just connect and let AI handle it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Connect Bank</h3>
              <img 
                src="/screenshots/bank.png"
                alt="Connect Bank with Stripe"
                className="w-full rounded-lg shadow-lg mb-4 border border-gray-200"
                loading="lazy"
              />
              <p className="text-gray-600 mb-3">
                One-click secure connection via Stripe. Works with 5,000+ banks.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Read-only access (can't move money)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Bank-level encryption</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Takes 2 minutes</span>
                </li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">AI Categorizes</h3>
              <img 
                src="/screenshots/transactions.png"
                alt="AI Categorizes Every Transaction"
                className="w-full rounded-lg shadow-lg mb-4 border border-gray-200"
                loading="lazy"
              />
              <p className="text-gray-600 mb-3">
                95% of transactions auto-categorized correctly. Review takes minutes, not hours.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>80+ smart keyword rules</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>One-click category changes</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>IRS-compliant categories</span>
                </li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Export to CPA</h3>
              <img 
                src="/screenshots/reports.png"
                alt="CPA-Grade Tax Reports"
                className="w-full rounded-lg shadow-lg mb-4 border border-gray-200"
                loading="lazy"
              />
              <p className="text-gray-600 mb-3">
                Professional 8-section PDF. Schedule C, ledgers, everything your CPA needs.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 text-left">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Branded professional PDF</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Email directly to accountant</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Or file yourself</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Time Savings Callout */}
          <div className="mt-12 text-center">
            <Card className="bg-emerald-50 border-2 border-emerald-200 inline-block">
              <CardContent className="px-8 py-6">
                <p className="text-2xl font-bold text-emerald-900">
                  <Clock className="w-6 h-6 inline mr-2" />
                  Save 10+ Hours of Tax Prep Time
                </p>
                <p className="text-emerald-700 mt-2">
                  Worth $300-$500 if you hired a CPA to do it
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* USP GRID - Light Gray Background for Contrast */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Freelancers Choose SolelyTax AI
            </h2>
            <p className="text-xl text-gray-600">
              Not just another tax tool - built specifically for 1099 contractors
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* USP 1: 4 AI Advisors */}
            <Card className="hover:shadow-2xl transition-shadow border-2 border-emerald-100">
              <CardContent className="pt-8 pb-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      4 AI Tax Advisors
                    </h3>
                    <p className="text-emerald-600 font-semibold">
                      vs Keeper's 1 general bot
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  Chat with a CPA for tax strategy, Financial Analyst for growth, Bookkeeper for records, AND Business Attorney for structure. Not just one generic assistant.
                </p>
                <img 
                  src="/screenshots/advisors.png"
                  alt="4 Specialized AI Advisors"
                  className="w-full rounded-lg shadow-md"
                  loading="lazy"
                />
              </CardContent>
            </Card>

            {/* USP 2: 95% AI Accuracy */}
            <Card className="hover:shadow-2xl transition-shadow border-2 border-blue-100">
              <CardContent className="pt-8 pb-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      95% AI Accuracy
                    </h3>
                    <p className="text-blue-600 font-semibold">
                      vs 90-92% industry standard
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  Our AI has 80+ keyword mappings and 4-level intelligent fallback. Spend 5 minutes reviewing, not 5 hours categorizing.
                </p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700 font-semibold">Confidence Score</span>
                    <span className="text-blue-600 font-bold">95%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-gradient-to-r from-emerald-500 to-blue-500 h-3 rounded-full" style={{ width: '95%' }}></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* USP 3: Spanish Support */}
            <Card className="hover:shadow-2xl transition-shadow border-2 border-purple-100">
              <CardContent className="pt-8 pb-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      English + Español
                    </h3>
                    <p className="text-purple-600 font-semibold">
                      Competitors don't offer Spanish
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  ¿Hablas español? Full Spanish interface and support. Rare in tax software. Serve 30% more of the 1099 contractor market.
                </p>
                <div className="flex gap-3">
                  <div className="flex-1 bg-slate-100 rounded-lg p-4 text-center">
                    <p className="text-2xl mb-1">🇺🇸</p>
                    <p className="text-sm font-semibold text-gray-700">English</p>
                  </div>
                  <div className="flex-1 bg-emerald-50 rounded-lg p-4 text-center border-2 border-emerald-300">
                    <p className="text-2xl mb-1">🇪🇸</p>
                    <p className="text-sm font-semibold text-emerald-700">Español</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* USP 4: CPA-Grade Reports */}
            <Card className="hover:shadow-2xl transition-shadow border-2 border-amber-100">
              <CardContent className="pt-8 pb-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      CPA-Grade Reports
                    </h3>
                    <p className="text-amber-600 font-semibold">
                      8-section professional package
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  Hand your CPA a professional tax package, not a messy spreadsheet. Includes Schedule C, W-2s, transaction ledgers, and documentation.
                </p>
                <img 
                  src="/screenshots/reports.png"
                  alt="Professional Tax Reports"
                  className="w-full rounded-lg shadow-md"
                  loading="lazy"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* OFFER STACK - What's Included */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything Included at $79/Year
            </h2>
            <p className="text-xl text-gray-600">
              No upsells. No hidden fees. Everything you need.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {[
              "Unlimited transactions & receipts",
              "Automatic bank sync (Stripe)",
              "4 AI tax advisors (CPA, Financial, Bookkeeper, Attorney)",
              "Real-time tax estimates (Federal + State + SE)",
              "Receipt OCR & smart upload",
              "Mileage tracking ($0.67/mile)",
              "CSV import with AI mapping",
              "CPA-grade 8-section PDF reports",
              "English + Spanish interface",
              "1099-NEC contractor tracking",
              "Quarterly estimate calculator",
              "Priority email support"
            ].map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-800">{feature}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={handleFoundingCTA}
              className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-12 py-6 rounded-xl text-xl font-bold shadow-xl"
            >
              <Crown className="w-6 h-6 mr-2" />
              Get All This for $79/Year
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
            <p className="text-gray-500 mt-4">
              Regular price: $228/year • You save: $149/year forever
            </p>
          </div>
        </div>
      </section>

      {/* FAQ - Objection Handling */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Common Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "Is my bank data secure?",
                a: "Yes. We use Stripe Financial Connections (bank-level encryption). We have read-only access - we can't move money or make transfers. All data encrypted with 256-bit SSL."
              },
              {
                q: "How is this different from Keeper Tax?",
                a: "Keeper costs $199/year. We're $79 for founders with the same core features PLUS 4 AI advisors (they have 1) AND Spanish support (they don't). Best for users who already have a CPA or want better data management."
              },
              {
                q: "Do I need to file through you?",
                a: "No! We generate professional tax packages your CPA will love. We're a data management tool, not a filing service (that keeps costs low). You can use your CPA or file yourself with our organized data."
              },
              {
                q: "What if I don't like it?",
                a: "30-day money-back guarantee, no questions asked. Cancel anytime, no contracts."
              },
              {
                q: "What happens after 100 founding spots are gone?",
                a: "Price goes to $99/year (early access) then $228/year (standard). Founders lock in $79 forever - it never increases."
              }
            ].map((faq, idx) => (
              <Card key={idx} className="border-2 border-gray-200 hover:border-emerald-300 transition-colors">
                <CardContent className="p-6">
                  <button
                    onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                    className="w-full text-left flex items-center justify-between"
                  >
                    <h3 className="text-lg font-bold text-gray-900 pr-4">
                      {faq.q}
                    </h3>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-600 flex-shrink-0 transition-transform ${faqOpen === idx ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {faqOpen === idx && (
                    <p className="text-gray-700 mt-4 leading-relaxed">
                      {faq.a}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA - Urgency Reinforcement */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <Crown className="w-16 h-16 text-amber-400 mx-auto mb-6" />
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Stop Missing Deductions?
          </h2>
          <p className="text-2xl text-slate-300 mb-4">
            Lock in <span className="text-amber-400 font-bold">$79/year forever</span>
          </p>
          <p className="text-lg text-slate-400 mb-8">
            Only {spotsRemaining} of 100 founding spots remaining
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              onClick={handleFoundingCTA}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-12 py-6 text-xl font-bold shadow-2xl"
            >
              <Crown className="w-6 h-6 mr-2" />
              Claim Your Spot - $79/Year
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
            
            <Button
              onClick={handleWaitlistCTA}
              variant="outline"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20 px-8 py-6 text-lg"
            >
              Join Waitlist (Free)
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-slate-300 text-sm">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              30-Day Guarantee
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              Cancel Anytime
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              Secure Stripe Payment
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-400">
            <div className="flex items-center gap-6">
              <button onClick={() => navigate('/terms')} className="hover:text-emerald-400 transition-colors">
                Terms
              </button>
              <button onClick={() => navigate('/privacy')} className="hover:text-emerald-400 transition-colors">
                Privacy
              </button>
              <a href="mailto:support@solelytax.ai" className="hover:text-emerald-400 transition-colors">
                Support
              </a>
            </div>
            <div>
              <p>© 2026 SolelyTax AI. All rights reserved.</p>
            </div>
          </div>
          <div className="mt-6 text-center">
            <div className="flex justify-center items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Shield className="w-3 h-3" />
                256-bit SSL
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3" />
                GDPR Compliant
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3" />
                Data Never Sold
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MetaLanding;
